import { Response } from 'miragejs';
import { ERROR_KEYS, RESPONSE_TIMERS } from '../const/serverConst';

/* The following HTTP Response cateogries are likely
not necessary for most use cases. 

    Informational Responses
    Successful Responses
    Redirection Responses

It is most likely that only responses in the 200-299 
range would be used and likely defined specifically 
for an app's unique purposes.
*/

/**
 * Generate an error response
 *
 * @param {number} htmlCode - The HTML error code for the response
 * @param {string|object} body - Custom response body
 * @returns A new Mirage JS Response. See https://miragejs.com/api/classes/response/
 */
const generateErrorResponse = (htmlCode, body) => {
    const responseHeaders = {
        ...(htmlCode === 429 && { 'Retry-After': RESPONSE_TIMERS.RETRY_AFTER }),
    };
    let responseBody = '';

    if (typeof body === 'string') {
        responseBody += body;
        responseHeaders['Content-Type'] = 'text/plain';
    }

    if (typeof body === 'object') {
        responseBody += JSON.stringify(body);
        responseHeaders['Content-Type'] = 'application/json';
    }

    return new Response(htmlCode, { ...responseHeaders }, responseBody);
};

// Client Error Responses
/**
 * Bad Request Error
 *
 * @param {string|object} body - Custom response body
 * @returns A new Mirage JS Response. See https://miragejs.com/api/classes/response/
 */
export const badRequestError = (body) => {
    return generateErrorResponse(400, body || 'Bad Request');
};

/**
 * Not Authorized Error
 *
 * @param {string|object} body - Custom response body
 * @returns A new Mirage JS Response. See https://miragejs.com/api/classes/response/
 */
export const notAuthorizedError = (body) => {
    return generateErrorResponse(401, body || 'Not Authorized');
};

/**
 * Forbidden Error
 *
 * @param {string|object} body - Custom response body
 * @returns A new Mirage JS Response. See https://miragejs.com/api/classes/response/
 */
export const forbiddenError = (body) => {
    return generateErrorResponse(403, body || 'Forbidden');
};

/**
 * Not Found Error
 *
 * @param {string|object} body - Custom response body
 * @returns A new Mirage JS Response. See https://miragejs.com/api/classes/response/
 */
export const notFoundError = (body) => {
    return generateErrorResponse(404, body || 'Not Found');
};

/**
 * Request Timeout Error
 *
 * @param {string|object} body - Custom response body
 * @returns A new Mirage JS Response. See https://miragejs.com/api/classes/response/
 */
export const requestTimeoutError = (body) => {
    return generateErrorResponse(408, body || 'Request Timeout');
};

/**
 * Too Many Requests Error
 *
 * @param {string|object} body - Custom response body
 * @returns A new Mirage JS Response. See https://miragejs.com/api/classes/response/
 */
export const tooManyRequestsError = (body) => {
    return generateErrorResponse(429, body || 'Too Many Requests');
};

// Server Error Responses

/**
 * Internal Server Error
 *
 * @param {string|object} body - Custom response body
 * @returns A new Mirage JS Response. See https://miragejs.com/api/classes/response/
 */
export const internalServerError = (body) => {
    return generateErrorResponse(500, body || 'Internal Server Error');
};

/**
 * Bad Gateway Error
 *
 * @param {string|object} body - Custom response body
 * @returns A new Mirage JS Response. See https://miragejs.com/api/classes/response/
 */
export const badGatewayError = (body) => {
    return generateErrorResponse(502, body || 'Bad Gateway');
};

/**
 * Service Unavailable Error
 *
 * @param {string|object} body - Custom response body
 * @returns A new Mirage JS Response. See https://miragejs.com/api/classes/response/
 */
export const serviceUnavailableError = (body) => {
    return generateErrorResponse(503, body || 'Service Unavailable');
};

/**
 * Gateway Timeout Error
 *
 * @param {string|object} body - Custom response body
 * @returns A new Mirage JS Response. See https://miragejs.com/api/classes/response/
 */
export const gatewayTimeoutError = (body) => {
    return generateErrorResponse(504, body || 'Gateway Timeout');
};

export const COMMON_ERROR_RESPONSES = {
    [ERROR_KEYS.BAD_REQUEST]: badRequestError,
    [ERROR_KEYS.NOT_AUTHORIZED]: notAuthorizedError,
    [ERROR_KEYS.FORBIDDEN]: forbiddenError,
    [ERROR_KEYS.NOT_FOUND]: notFoundError,
    [ERROR_KEYS.REQUEST_TIMEOUT]: requestTimeoutError,
    [ERROR_KEYS.TOO_MANY_REQUESTS]: tooManyRequestsError,
    [ERROR_KEYS.INTERNAL_SERVER_ERROR]: internalServerError,
    [ERROR_KEYS.BAD_GATEWAY]: badGatewayError,
    [ERROR_KEYS.SERVICE_UNAVAILABLE]: serviceUnavailableError,
    [ERROR_KEYS.GATEWAY_TIMEOUT]: gatewayTimeoutError,
};
